import type { Service } from './types';

/* ============================================================================
 * SERVICES — nine pages, and deliberately not ninety.
 *
 * THE TAXONOMY. Each entry below is a page with a distinct buyer and a distinct
 * search behind it. Everything else a client might name is folded into the
 * page it belongs to, rather than given a page of its own that would say the
 * same thing with the noun swapped:
 *
 *   UI/UX design ........... inside websites and apps (it is how they are made)
 *   mobile apps ............ inside apps & custom software (one codebase, same backend)
 *   maintenance ............ the care plan, on /process
 *   API integrations,
 *   data pipelines ......... inside AI & automation
 *   short-form video, reels  inside social media
 *   lead generation, CRO,
 *   analytics .............. inside performance marketing
 *   Google Business Profile  inside SEO & local search
 *
 * A thin page per keyword is what the competitors in Tricity do — twenty-odd
 * service pages and a clone per city. The bet here is the opposite: fewer
 * pages, each one genuinely the best answer to its question.
 *
 * TWO GROUPS, because the studio does two different jobs and a visitor who
 * came for one should see the other exists without wading through it: BUILD is
 * a project with an end, GROW is a monthly service. They are quoted separately.
 *
 * ON HONESTY in the Grow group. There is no marketing account the studio has
 * run for a client that it can show yet. So every line there is written as an
 * offer — what the work is and how it is done — and never as a result. No
 * follower counts, no ROAS, no "we grew". The `work` field on those services is
 * either absent or frames something adjacent honestly; it must never imply a
 * campaign we did not run.
 *
 * Tone follows the note at the top of src/data/studio.ts: plain first, warm second,
 * clever never. British spelling, as in the rest of the site.
 * ========================================================================== */

export const SERVICES: Service[] = [
  /* ------------------------------------------------------------------ BUILD */
  {
    slug: 'website-development',
    group: 'build',
    name: 'Website development',
    short: 'Websites',
    icon: 'Globe',
    summary:
      'Company sites, landing pages and redesigns. Quick to load, easy for you to update, and built so search engines can actually read them.',
    includes: [
      'Company websites',
      'Landing pages',
      'Website redesigns',
      'Editing you can do yourself',
      'Technical SEO built in',
      'Hosting and care',
    ],
    seo: {
      title: 'Website Development Company in Mohali & Chandigarh | Bitbew',
      description:
        'Custom websites for businesses in Mohali, Chandigarh and across India — fast on a phone, easy to update, built to be found on Google, and owned by you.',
    },
    hero: {
      eyebrow: 'Website development in Mohali & Chandigarh',
      title: 'Websites built to be found and used',
      lede:
        'Company sites, landing pages and redesigns, written in real code rather than assembled from a theme. Quick on a cheap phone, readable by Google, easy for you to update — and in your name from the first day.',
    },
    artifact: 'sitemap',
    whatsapp: "Hi Bitbew — I'd like to talk about a website.",
    problems: {
      title: 'Sound familiar?',
      lede: 'Most people who get in touch about a website are not starting from nothing. They are starting from one of these.',
      items: [
        {
          head: 'It takes an age to load on a phone',
          tail: 'And most of your visitors are on a phone, on mobile data, deciding in a few seconds whether to wait.',
        },
        {
          head: 'Nobody finds it on Google',
          tail: 'Search engines cannot rank what they cannot read, and sites built from heavy page builders often give them very little.',
        },
        {
          head: 'You cannot change a price without calling someone',
          tail: 'Every small edit becomes a request, a wait, and sometimes an invoice.',
        },
        {
          head: 'It does not look like the business you run now',
          tail: 'It was made for the company you were a few years ago, and it is quietly underselling the one you are today.',
        },
        {
          head: 'You are not sure who owns it',
          tail: 'The domain, the hosting or the login sits in someone else’s account, and getting it back would be a project of its own.',
        },
      ],
    },
    offer: {
      title: 'What we build',
      lede: 'Every site is designed for the business in front of us and built by the person you talk to. No theme marketplace, and no page builder layered on top.',
      items: [
        {
          title: 'Company websites',
          body: 'The site that explains what you do, who you do it for and how to reach you — structured so each service has a page that can be found on its own.',
        },
        {
          title: 'Landing pages',
          body: 'One page, one job: an offer, a launch or an ad campaign. Built to load instantly, and measured so you can tell whether it is working.',
        },
        {
          title: 'Redesigns',
          body: 'A new site that keeps what the old one earned. The pages Google already knows are redirected properly, so you do not start again from zero.',
        },
        {
          title: 'Editing you can do yourself',
          body: 'Text, prices, photos, services and posts. Wherever something changes often, you get a simple way to change it without us.',
        },
        {
          title: 'Search built in',
          body: 'Fast pages, clean headings, proper titles and descriptions, a sitemap, structured data and your local details — the technical side of SEO, done while we build rather than bolted on after.',
        },
        {
          title: 'Forms that go somewhere',
          body: 'Enquiries land where you will actually see them — your inbox, WhatsApp, a spreadsheet or your CRM — instead of a mailbox nobody checks.',
        },
      ],
    },
    fit: {
      good: [
        'Service businesses, clinics, consultancies and manufacturers who need to be taken seriously online',
        'Companies whose current site is slow, dated or impossible to edit',
        'Anyone launching something who needs a landing page that can carry paid traffic',
      ],
      notFor: [
        'A one-off event page you will delete next month — a free builder is fine for that',
        'Sites that need a large in-house team making changes every day',
      ],
    },
    steps: [
      {
        title: 'Map it',
        body: 'We list the pages, what each one is for and what a visitor should do next. You see the whole structure before any design.',
      },
      {
        title: 'Design it',
        body: 'Real layouts with your real words and photos, on a private link, so you judge the thing itself rather than a mockup of it.',
      },
      {
        title: 'Build it',
        body: 'Written in code, tested on real phones, checked for speed and accessibility. Every page gets its own title, description and structured data.',
      },
      {
        title: 'Launch it',
        body: 'Domain, hosting, analytics and Search Console set up in your accounts, old addresses redirected, and a walkthrough of how to make changes.',
      },
    ],
    stack: [
      { label: 'Built with', items: ['Astro', 'React', 'TypeScript', 'Tailwind CSS'] },
      { label: 'Content', items: ['Markdown', 'Supabase', 'A headless CMS where it earns its place'] },
      { label: 'Hosted on', items: ['Cloudflare', 'Vercel', 'Netlify'] },
    ],
    work: {
      projects: ['socialsurfers', 'silomin'],
      note: 'Social Surfers is a company site in the purest sense: sixteen services to explain, and an enquiry form that files every lead into the agency’s own spreadsheet.',
    },
    faqs: [
      {
        id: 'web-time',
        question: 'How long does a website take?',
        answer: [
          'A company site is usually a few weeks from the first call to launch, and a single landing page can be days. The written plan gives you real dates, broken into stages.',
        ],
      },
      {
        id: 'web-edit',
        question: 'Will I be able to update it myself?',
        answer: [
          'Yes, for the things that change: text, prices, photos, services and posts. We set that up wherever it is needed and show you how it works at handover.',
          'Structural changes — a new kind of section, a new feature — are a quick job for us, and small ones are covered by the care plan for the first three months.',
        ],
      },
      {
        id: 'web-wordpress',
        question: 'Do you use WordPress or a template?',
        answer: [
          'No. We build in modern, well-known tools — mostly Astro and React — which is a large part of why the sites load quickly. If you already run WordPress and it works for you, we will say so rather than push a rebuild.',
        ],
      },
      {
        id: 'web-rank',
        question: 'Will it rank on Google?',
        answer: [
          'We build in everything a search engine needs to read and trust a site: speed, structure, titles, structured data and local details. Nobody honest can promise a ranking, because that also depends on your competitors and your content. If search matters to you, our SEO service carries on where the build stops.',
        ],
      },
      {
        id: 'web-cost',
        question: 'What does a website cost?',
        answer: [
          'It depends on how many pages there are, what they need to do, and whether the words and photos are ready. We quote a fixed price per stage after the first call. No hourly billing.',
        ],
      },
    ],
    related: ['seo', 'branding', 'ecommerce-development'],
    cta: {
      title: 'Tell us about the site',
      body: 'Send the current address if there is one, or a line about the business if there is not. We will come back with an honest view of what it needs.',
    },
  },

  {
    slug: 'ecommerce-development',
    group: 'build',
    name: 'E-commerce development',
    short: 'Online stores',
    icon: 'ShoppingCart',
    summary:
      'Online shops with a product catalogue, payments, discount codes, delivery, order tracking and the emails that go out after a sale.',
    includes: [
      'Custom online stores',
      'Razorpay and UPI checkout',
      'Shipping integrations',
      'Order tracking',
      'Admin panel for orders and stock',
      'Advice on Shopify versus custom',
    ],
    seo: {
      title: 'E-commerce Website Development for D2C Brands in India | Bitbew',
      description:
        'Online stores for Indian D2C brands: Razorpay and UPI checkout, shipping, coupons, order tracking and an admin panel you run yourself. Built in Mohali.',
    },
    hero: {
      eyebrow: 'E-commerce website development',
      title: 'Online stores that sell direct',
      lede:
        'A shop of your own instead of a listing on someone else’s marketplace: catalogue, checkout, payments, shipping, order tracking, and the admin side you run it from every day. We have built them for a skincare brand and a handmade label, and both are live.',
    },
    artifact: 'order',
    whatsapp: "Hi Bitbew — I'd like to talk about an online store.",
    problems: {
      title: 'Why brands come to us',
      lede: 'Usually because one of these has started to cost real money.',
      items: [
        {
          head: 'Marketplace fees eat the margin',
          tail: 'And the customer belongs to the marketplace. You never get their details, so you can never sell to them again.',
        },
        {
          head: 'Checkout loses people',
          tail: 'Too many steps, the wrong payment options, a page that stalls on mobile data. Every abandoned cart is an ad you already paid for.',
        },
        {
          head: 'Orders live in WhatsApp and a notebook',
          tail: 'Fine at ten orders a week. Somewhere past that, things get missed and customers start asking where their parcel is.',
        },
        {
          head: 'The store fights the product',
          tail: 'Made to order, one-offs, bundles, sizes — templates assume a catalogue that looks like everyone else’s.',
        },
      ],
    },
    offer: {
      title: 'What a store includes',
      lede: 'Everything between someone seeing your product and the parcel reaching their door — and the tools to run it without us.',
      items: [
        {
          title: 'Storefront and catalogue',
          body: 'Product pages that load fast and sell: real photos, variants, stock levels and reviews, in a catalogue shaped around what you actually make.',
        },
        {
          title: 'Checkout and payments',
          body: 'Razorpay with UPI, cards, net banking and wallets, cash on delivery if you offer it, and coupon codes. As few steps as possible, all of them quick on a phone.',
        },
        {
          title: 'Shipping and tracking',
          body: 'Shipping partners such as Shiprocket wired in, and a tracking page so customers can check on an order themselves instead of messaging you.',
        },
        {
          title: 'Emails after the sale',
          body: 'Order confirmations, shipping updates and the other messages a customer expects, sent automatically and written in your voice.',
        },
        {
          title: 'Admin panel',
          body: 'Your side of the shop: orders, stock, products, customers and coupons in one place, with exports for when your accountant asks.',
        },
        {
          title: 'Accounts and reviews',
          body: 'Customer accounts, order history and product reviews, so people come back and the next customer has something to trust.',
        },
      ],
    },
    fit: {
      good: [
        'D2C brands that want their own customers and their own margin',
        'Makers and small labels with unusual catalogues — made to order, one-offs, custom pieces',
        'Brands outgrowing a marketplace listing or a template store',
      ],
      notFor: [
        'Testing an idea with five products this weekend — start on Shopify or Instagram, and come back when it works',
        'Large multi-vendor marketplaces',
      ],
    },
    steps: [
      {
        title: 'Catalogue first',
        body: 'We map products, variants, prices, shipping rules and how you fulfil orders. The store is designed around the answers.',
      },
      {
        title: 'Checkout and payments',
        body: 'Payment gateway, shipping and order emails set up and tested with real transactions before anything else is polished.',
      },
      {
        title: 'The storefront',
        body: 'Product and category pages built and reviewed on a private link, with your real photos and your real words.',
      },
      {
        title: 'Launch and handover',
        body: 'Test orders run end to end, your accounts connected, and a walkthrough of the admin so you can run the shop from the first day.',
      },
    ],
    stack: [
      { label: 'Storefront', items: ['React', 'TypeScript', 'Astro'] },
      { label: 'Backend', items: ['Supabase', 'Postgres'] },
      { label: 'Payments & shipping', items: ['Razorpay', 'UPI', 'Cash on delivery', 'Shiprocket'] },
    ],
    work: {
      projects: ['silomin', 'jcharms'],
      note: 'Two stores with very different catalogues: a clinical skincare range, and handmade crochet where no two pieces are the same.',
    },
    faqs: [
      {
        id: 'shop-shopify',
        question: 'Should I use Shopify or have a store built?',
        answer: [
          'Shopify is a good answer for a lot of brands, especially early on, and we will tell you if it is the right one for you. A custom store starts to make sense when monthly app costs and fees add up, when your catalogue does not fit a template, or when you want full control of checkout and your customer data. The guide below goes through the trade-off properly.',
        ],
      },
      {
        id: 'shop-payments',
        question: 'Which payment methods can customers use?',
        answer: [
          'Through Razorpay: UPI, cards, net banking and wallets. Cash on delivery can sit alongside it if you want to offer it. Payments go straight into your own Razorpay account.',
        ],
      },
      {
        id: 'shop-admin',
        question: 'Can I manage products and orders myself?',
        answer: [
          'Yes. Every store comes with an admin panel for products, stock, orders, customers and coupons, and we walk you through it at handover.',
        ],
      },
      {
        id: 'shop-marketing',
        question: 'Can you bring in the customers too?',
        answer: [
          'Yes — ads, social media, UGC and creator campaigns are the other half of the studio, and they are quoted separately. Plenty of brands take the store and run their own marketing, which is fine too.',
        ],
      },
      {
        id: 'shop-time',
        question: 'How long does a store take?',
        answer: [
          'A straightforward store is usually a few weeks. Unusual catalogues, connections to systems you already use, or large product imports take longer. You get real dates in the written plan.',
        ],
      },
    ],
    related: ['performance-marketing', 'ugc-influencer-marketing', 'branding'],
    cta: {
      title: 'Tell us what you sell',
      body: 'A link to your Instagram or your current shop is plenty. We will come back with how we would set the store up, and what it would take.',
    },
  },

  {
    slug: 'software-development',
    group: 'build',
    name: 'Apps & custom software',
    short: 'Apps & software',
    icon: 'LayoutDashboard',
    summary:
      'Web apps, mobile apps, admin panels, dashboards and portals — software for the way your business actually runs. Usually the job a spreadsheet has outgrown.',
    includes: [
      'Web applications',
      'Android and iOS apps',
      'Admin panels and dashboards',
      'Client and staff portals',
      'Booking, CRM and invoicing tools',
      'API integrations',
    ],
    seo: {
      title: 'Custom Software & Mobile App Development in Mohali | Bitbew',
      description:
        'Web apps, Android and iOS apps, dashboards, admin panels, portals and internal tools, built in TypeScript by a small studio in Mohali. You own the code.',
    },
    hero: {
      eyebrow: 'Custom software & app development',
      title: 'Software for the way you actually work',
      lede:
        'For when the spreadsheet has outgrown itself, the off-the-shelf tool almost fits, or an idea needs to become a product: web apps, mobile apps, dashboards and portals, written in plain, typed code that any good developer could pick up after us.',
    },
    artifact: 'admin',
    whatsapp: "Hi Bitbew — I'd like to talk about building some software.",
    problems: {
      title: 'Where it usually starts',
      lede: 'Nobody wakes up wanting software. They want one of these to stop.',
      items: [
        {
          head: 'The business runs on a spreadsheet nobody trusts',
          tail: 'Five versions, three people editing, and one formula that everything depends on.',
        },
        {
          head: 'You pay for five tools that each do a fifth of the job',
          tail: 'And somebody copies data between them by hand every week.',
        },
        {
          head: 'Clients keep asking for updates you have to type out',
          tail: 'A portal where they can see status, files and invoices would answer most of those messages for you.',
        },
        {
          head: 'You have an idea for a product',
          tail: 'And it needs building properly enough to put in front of paying users, without a six-month agency project.',
        },
      ],
    },
    offer: {
      title: 'What we build',
      lede: 'All of it in TypeScript on well-known, deliberately boring tools, documented, and set up in your own accounts.',
      items: [
        {
          title: 'Web applications',
          body: 'Anything that runs in a browser and needs accounts, data and logic behind it — from a booking system to a full SaaS product.',
        },
        {
          title: 'Mobile apps',
          body: 'Android and iOS from one codebase, so you pay for one build, sharing a backend with your web app.',
        },
        {
          title: 'Admin panels and dashboards',
          body: 'The screens you run the business from: orders, stock, bookings, invoices, reports, and who is allowed to see what.',
        },
        {
          title: 'Portals',
          body: 'A private area for your clients, dealers or staff, with their own logins, documents, status updates and payments.',
        },
        {
          title: 'Integrations',
          body: 'Your software talking to what you already use — payment gateways, WhatsApp, Google Sheets, accounting tools, CRMs — through their APIs.',
        },
        {
          title: 'First versions',
          body: 'The smallest version of a product real users can pay for, built so it can grow rather than be thrown away.',
        },
      ],
    },
    fit: {
      good: [
        'Businesses whose process lives in spreadsheets, WhatsApp groups and somebody’s memory',
        'Founders who need a first version built properly, before there is an in-house team',
        'Teams paying for several tools that each almost fit',
      ],
      notFor: [
        'Anything an existing product already does well and cheaply — we will point you to it',
        'Very large systems that need a big team from the first day',
      ],
    },
    steps: [
      {
        title: 'Understand the work',
        body: 'We look at the process as it runs today — who does what, where the data lives, what goes wrong — before suggesting a single screen.',
      },
      {
        title: 'Plan the first version',
        body: 'The smallest thing that removes the most pain, written down with the screens, the data and a price per stage.',
      },
      {
        title: 'Build in slices',
        body: 'Each piece goes live on a private link as it is finished, so the people who will use it can test it while changes are still cheap.',
      },
      {
        title: 'Roll it out',
        body: 'Existing data moved across, people shown how it works, and monitoring switched on so we see problems before your team does.',
      },
    ],
    stack: [
      { label: 'Front end', items: ['TypeScript', 'React', 'Next.js', 'React Native'] },
      { label: 'Back end', items: ['Node.js', 'Python', 'FastAPI', 'Postgres', 'Supabase'] },
      { label: 'Infrastructure', items: ['Cloudflare', 'Vercel', 'GitHub Actions'] },
    ],
    work: {
      projects: ['silomin', 'jcharms'],
      lab: ['interviewsim'],
      note: 'Both stores run on admin panels we built — orders, stock, customers and exports. InterviewSim is our own prototype, a live voice application, and is labelled as one.',
    },
    faqs: [
      {
        id: 'sw-mobile',
        question: 'Do you build mobile apps as well?',
        answer: [
          'Yes — Android and iOS from one codebase, usually alongside a web app that shares the same backend. If a mobile-friendly web app would do the job for less, we will tell you.',
        ],
      },
      {
        id: 'sw-existing',
        question: 'Can you work on software someone else built?',
        answer: [
          'Often, yes. We start with a short paid review of the code so we can tell you honestly whether it is worth building on, or cheaper to replace.',
        ],
      },
      {
        id: 'sw-cost',
        question: 'What does custom software cost?',
        answer: [
          'It depends entirely on scope, which is why the first version is planned and priced in fixed stages before any code is written. No hourly billing, and no surprise invoice at the end.',
        ],
      },
      {
        id: 'sw-after',
        question: 'What happens after launch?',
        answer: [
          'The first three months of care are included: monitoring, security updates, backups and small changes. After that it carries on monthly or yearly if you want it to.',
        ],
      },
    ],
    related: ['ai-automation', 'website-development', 'ecommerce-development'],
    cta: {
      title: 'Describe the process',
      body: 'Tell us what the work looks like today and where it hurts — a voice note on WhatsApp is fine. We will tell you whether software is the right answer at all.',
    },
  },

  {
    slug: 'ai-automation',
    group: 'build',
    name: 'AI & automation',
    short: 'AI & automation',
    icon: 'Bot',
    summary:
      'AI agents that answer customers and handle enquiries, and automations that move data, send reminders and write the reports so a person does not have to.',
    includes: [
      'AI agents and chatbots',
      'WhatsApp and website assistants',
      'Workflow automation with n8n',
      'AI inside the tools you use',
      'Data pipelines and reporting',
      'API integrations',
    ],
    seo: {
      title: 'AI Automation & AI Agent Development in India | Bitbew',
      description:
        'AI agents, WhatsApp and website chatbots, n8n workflow automation and data pipelines for Indian businesses — built by engineers in Mohali, and yours to keep.',
    },
    hero: {
      eyebrow: 'AI automation & AI agents',
      title: 'AI and automation that does the busywork',
      lede:
        'Assistants that answer customers from your own products and documents, and workflows that move data, chase reminders and write the reports — so software does the repetitive part of the week, and your people do the part that needs a person.',
    },
    artifact: 'workflow',
    whatsapp: "Hi Bitbew — I'd like to talk about automating something.",
    problems: {
      title: 'Good candidates for automation',
      lede: 'If a task is repetitive, follows rules and happens on a computer, there is a fair chance software can do it — or most of it.',
      items: [
        {
          head: 'Enquiries sit unanswered overnight',
          tail: 'People ask the same ten questions at eleven at night. An assistant trained on your own answers can reply straight away and hand the rest to a person.',
        },
        {
          head: 'Someone copies data from one place to another',
          tail: 'Form to spreadsheet, spreadsheet to invoice, invoice to accounts. Every copy is time, and a chance to get it wrong.',
        },
        {
          head: 'Reports take a morning to put together',
          tail: 'Numbers pulled from four places and pasted into one, every week. That can arrive in your inbox on its own.',
        },
        {
          head: 'Follow-ups depend on someone remembering',
          tail: 'Payment reminders, review requests, renewal nudges — the easiest things to automate, and the most expensive to forget.',
        },
      ],
    },
    offer: {
      title: 'What we build',
      lede: 'We start from the task, not the technology. Sometimes the answer is an AI agent; often it is a plain workflow with no AI in it at all, which is cheaper and more reliable.',
      items: [
        {
          title: 'AI agents and chatbots',
          body: 'Assistants on your website or WhatsApp that answer from your own products, prices and documents, take bookings and qualify enquiries — and know when to hand over to a person.',
        },
        {
          title: 'Workflow automation',
          body: 'The repetitive chains of work, built in n8n or in code: new lead to CRM to WhatsApp to team alert; order to invoice to accounts; form to sheet to follow-up.',
        },
        {
          title: 'AI inside your tools',
          body: 'Summaries, classification, drafting and extraction added to software you already use — reading invoices, tagging enquiries, drafting replies for a person to approve.',
        },
        {
          title: 'Data pipelines and reports',
          body: 'Data from your store, ads, sheets and tools pulled into one place, cleaned, and turned into the dashboard or weekly report you actually read.',
        },
        {
          title: 'Content pipelines',
          body: 'Scheduled, templated content made and published by software — the same kind of pipeline we built for our own reels account.',
        },
        {
          title: 'Integrations that fail loudly',
          body: 'APIs connected with retries, logging and alerts, so an automation that breaks tells someone instead of failing quietly for a month.',
        },
      ],
    },
    fit: {
      good: [
        'Teams losing hours every week to copying, chasing and reporting',
        'Businesses answering the same customer questions all day',
        'Anyone who has tried a no-code automation that keeps breaking',
      ],
      notFor: [
        'Replacing judgement calls that need a person — we will tell you where AI should stop',
        'Tasks that happen twice a year; those are cheaper done by hand',
      ],
    },
    steps: [
      {
        title: 'Find the task',
        body: 'We list the repetitive work, how often it happens and what it costs, and pick the one worth automating first.',
      },
      {
        title: 'Prove it small',
        body: 'One workflow built and run on real data, with a person checking the output before anything goes out unsupervised.',
      },
      {
        title: 'Harden it',
        body: 'Retries, logging, alerts and a clear way to switch it off. AI steps get guardrails, so a bad response cannot do damage.',
      },
      {
        title: 'Hand it over',
        body: 'Documented and running in your accounts, with a record of what it did and when it last failed.',
      },
    ],
    stack: [
      { label: 'Automation', items: ['n8n', 'Google Apps Script', 'GitHub Actions', 'Webhooks'] },
      { label: 'AI models', items: ['OpenAI', 'Anthropic Claude', 'Google Gemini', 'Whisper'] },
      { label: 'Data', items: ['Postgres', 'Supabase', 'Google Sheets', 'Python'] },
      { label: 'Channels', items: ['WhatsApp Business API', 'Website chat', 'Email', 'Instagram API'] },
    ],
    work: {
      projects: ['socialsurfers'],
      lab: ['reels', 'interviewsim'],
      note: 'The Social Surfers enquiry form is a small, real automation: every lead files itself into the agency’s own spreadsheet. The two prototypes are ours, unpaid, and labelled as such — they are where we test how far this can go.',
    },
    faqs: [
      {
        id: 'ai-need',
        question: 'Do I need AI, or just automation?',
        answer: [
          'Often just automation. If a task follows rules, a plain workflow is cheaper, faster and more predictable. We use AI where the input is messy — free-text messages, documents, voice — and keep it out of the steps that must be exactly right.',
        ],
      },
      {
        id: 'ai-models',
        question: 'Which AI models do you use?',
        answer: [
          'Whichever suits the job: OpenAI, Anthropic, Google or an open model. We build so the provider can be swapped with a configuration change rather than a rewrite, because prices and quality move every few months.',
        ],
      },
      {
        id: 'ai-data',
        question: 'Is my data safe?',
        answer: [
          'Automations run in your own accounts, with access limited to what each step needs. We tell you exactly which services see which data, and where it matters we can keep sensitive data away from third-party AI entirely.',
        ],
      },
      {
        id: 'ai-break',
        question: 'What happens when an automation breaks?',
        answer: [
          'It tells someone. Every workflow we build logs what it did and alerts on failure, and the care plan covers fixing it when an outside service changes how it works.',
        ],
      },
      {
        id: 'ai-whatsapp',
        question: 'Can the assistant answer on WhatsApp?',
        answer: [
          'Yes, through the official WhatsApp Business API. Meta charges for business messaging on its own terms, separately from us, and we will walk you through how that works before you commit.',
        ],
      },
    ],
    related: ['software-development', 'website-development', 'performance-marketing'],
    cta: {
      title: 'Tell us what eats the week',
      body: 'Describe the task in a sentence — what comes in, what somebody does with it, where it goes. We will tell you honestly whether it is worth automating.',
    },
  },

  /* ------------------------------------------------------------------- GROW */
  {
    slug: 'branding',
    group: 'grow',
    name: 'Branding & logo design',
    short: 'Branding',
    icon: 'PenTool',
    summary:
      'A logo, colours, type and the rules for using them — set up once, properly, so everything you make afterwards looks like it came from the same place.',
    includes: [
      'Logo design',
      'Colours and typography',
      'Brand guidelines',
      'Social media kit',
      'Stationery and templates',
      'Packaging and label layouts',
    ],
    seo: {
      title: 'Branding & Logo Design in Mohali & Chandigarh | Bitbew',
      description:
        'Logo design and brand identity for new businesses and D2C brands in Mohali, Chandigarh and across India — colours, type, guidelines and ready-to-use files.',
    },
    hero: {
      eyebrow: 'Branding & logo design',
      title: 'A brand set up properly, once',
      lede:
        'A logo, colours, type and a short set of rules for using them, delivered as files your printer, your developer and whoever runs your Instagram can all use. Nothing precious — just enough identity that everything you make looks like it belongs together.',
    },
    artifact: 'brandkit',
    whatsapp: "Hi Bitbew — I'd like to talk about a logo and branding.",
    problems: {
      title: 'When it is time',
      lede: 'Branding rarely feels urgent until one of these happens.',
      items: [
        {
          head: 'You are launching and the name is all you have',
          tail: 'And the website, the packaging and the Instagram all need something to be consistent with.',
        },
        {
          head: 'Every post and flyer looks different',
          tail: 'Because each one was made by a different person with no rules to follow.',
        },
        {
          head: 'The logo only exists as a blurry JPG',
          tail: 'It breaks on a dark background, turns to mush at small sizes, and nobody has the original file.',
        },
        {
          head: 'The business grew up and the brand did not',
          tail: 'It still looks like the side project it started as, and customers can tell.',
        },
      ],
    },
    offer: {
      title: 'What you get',
      lede: 'Deliberately practical. You leave with decisions made and files that work, not a mood board.',
      items: [
        {
          title: 'Logo',
          body: 'A primary logo, a compact mark for small spaces, and versions for light and dark backgrounds — drawn as vectors, so it stays sharp at any size.',
        },
        {
          title: 'Colour and type',
          body: 'A small, tested palette and a pair of typefaces, with contrast checked so text stays readable on screen and in print.',
        },
        {
          title: 'Guidelines',
          body: 'A short document showing how to use it all — spacing, sizes, what not to do — written for whoever makes your next flyer.',
        },
        {
          title: 'Social media kit',
          body: 'Profile pictures, highlight covers and post templates, so the feed looks consistent from the first week.',
        },
        {
          title: 'Everyday templates',
          body: 'Business cards, letterheads, invoices and presentation slides in your identity.',
        },
        {
          title: 'Packaging and labels',
          body: 'For product brands, label and packaging layouts that carry the identity, prepared to your printer’s specifications.',
        },
      ],
    },
    fit: {
      good: [
        'New businesses and D2C brands about to launch',
        'Businesses whose visual identity grew by accident',
        'Anyone about to spend on a website, packaging or ads who wants it all to match',
      ],
      notFor: [
        'Large rebrands that need consumer research across several markets',
        'Naming — we will happily think it through with you, but we are not a naming agency',
      ],
    },
    steps: [
      {
        title: 'Listen',
        body: 'Who you sell to, who you compete with, what the brand must say, and what it must never say.',
      },
      {
        title: 'Explore',
        body: 'A small number of genuinely different directions, shown in use — on a product, a post, a shopfront — rather than floating on a white page.',
      },
      {
        title: 'Refine',
        body: 'One direction taken to finished: the logo drawn properly, colours and type chosen, and all of it tested at every size.',
      },
      {
        title: 'Deliver',
        body: 'Every file in every format you need, organised, with the guidelines — and yours outright.',
      },
    ],
    stack: [{ label: 'Files you receive', items: ['SVG', 'PNG', 'PDF', 'Print-ready CMYK', 'Editable source files'] }],
    faqs: [
      {
        id: 'brand-options',
        question: 'How many logo options will I see?',
        answer: [
          'A few genuinely different directions rather than twenty variations of one idea. Each is shown in use, so you choose based on how it will actually look on your product and your feed.',
        ],
      },
      {
        id: 'brand-own',
        question: 'Do I own the logo?',
        answer: ['Yes, completely — the files, the source and the rights, handed over at the end.'],
      },
      {
        id: 'brand-web',
        question: 'Can you build the website as well?',
        answer: [
          'Yes. Branding and a website from the same studio saves a round of translation: the people who drew the identity are the ones putting it on screen.',
        ],
      },
      {
        id: 'brand-time',
        question: 'How long does branding take?',
        answer: [
          'Usually a few weeks, most of it spent exploring and on your feedback. Rushing that part is the most common way to end up with a logo you replace within a year.',
        ],
      },
      {
        id: 'brand-trademark',
        question: 'Do you register trademarks?',
        answer: [
          'No — we are not lawyers. We check for obvious conflicts while we design, and can point you to someone who files trademarks.',
        ],
      },
    ],
    related: ['website-development', 'social-media-marketing', 'ecommerce-development'],
    cta: {
      title: 'Tell us what you are starting',
      body: 'A line about the business and who it is for is enough to begin. If you already have a logo you like, say so — sometimes it only needs tidying.',
    },
  },

  {
    slug: 'social-media-marketing',
    group: 'grow',
    name: 'Social media marketing',
    short: 'Social media',
    icon: 'MessageSquare',
    summary:
      'Instagram, Facebook, YouTube and LinkedIn run week to week: a plan, the reels and posts themselves, the publishing, and answering what comes back.',
    includes: [
      'Social media management',
      'Monthly content plan',
      'Reels and short-form video',
      'Posts, carousels and captions',
      'Replies to comments and DMs',
      'Plain monthly reporting',
    ],
    seo: {
      title: 'Social Media Marketing Agency in Mohali & Chandigarh | Bitbew',
      description:
        'Social media management for businesses in Mohali, Chandigarh and across India: a monthly plan, reels and short-form video, posts, replies and plain reports.',
    },
    hero: {
      eyebrow: 'Social media marketing & content',
      title: 'Social media, handled every week',
      lede:
        'A plan for what goes out and when, the reels, carousels and captions themselves, the posting, and someone answering the comments and DMs — agreed once a month, so you can stop thinking about it for the rest of it.',
    },
    artifact: 'calendar',
    whatsapp: "Hi Bitbew — I'd like to talk about social media.",
    problems: {
      title: 'Sound familiar?',
      lede: 'Most businesses know social media matters. The trouble is doing it every single week.',
      items: [
        {
          head: 'Posting happens when someone remembers',
          tail: 'Three posts in a week, then nothing for a month. Consistency is most of the game, and it is the hardest part.',
        },
        {
          head: 'Reels are where the reach is, and nobody has time to make them',
          tail: 'Short video takes planning, filming, editing and captions. It does not fit around running a business.',
        },
        {
          head: 'Comments and messages go unanswered',
          tail: 'Every one of them is a customer who asked a question in public.',
        },
        {
          head: 'You cannot tell whether any of it works',
          tail: 'Likes are not sales, and nobody is connecting the posts to the enquiries.',
        },
      ],
    },
    offer: {
      title: 'What running it includes',
      lede: 'One monthly retainer covering the plan, the content and the running of the accounts. What goes into it is agreed up front and written down.',
      items: [
        {
          title: 'A monthly plan',
          body: 'Themes, formats and a calendar for the month ahead, sent for your approval before anything is made.',
        },
        {
          title: 'Reels and short-form video',
          body: 'Scripted, filmed or assembled, edited and captioned for Instagram Reels, YouTube Shorts and Facebook — from footage we shoot, footage you send, or both.',
        },
        {
          title: 'Posts and carousels',
          body: 'Graphics, carousels, stories and thumbnails in your brand, with captions written for each platform.',
        },
        {
          title: 'Publishing',
          body: 'Scheduled and posted at the right times on Instagram, Facebook, LinkedIn and YouTube.',
        },
        {
          title: 'Community management',
          body: 'Replies to comments and DMs in your voice, with anything that needs you passed on the same day.',
        },
        {
          title: 'Plain reporting',
          body: 'A short monthly report: what went out, what people responded to, what brought in enquiries, and what changes next month.',
        },
      ],
    },
    fit: {
      good: [
        'Clinics, restaurants, studios, schools and service businesses that need to show up consistently',
        'D2C brands that need a steady stream of content for organic and paid',
        'Owners who know what to say, but not when they will find the time to say it',
      ],
      notFor: [
        'Anyone expecting guaranteed viral growth — nobody honest can promise that',
        'Brands whose approval chain is longer than the content calendar',
      ],
    },
    steps: [
      {
        title: 'Audit and plan',
        body: 'We look at your accounts, your competitors and what your customers respond to, and agree the platforms, formats and monthly volume.',
      },
      {
        title: 'Shoot and make',
        body: 'A batch of content produced at the start of each month — filmed with you, made from what you send, or designed from scratch.',
      },
      {
        title: 'Post and reply',
        body: 'Published on schedule, with comments and DMs answered and anything important passed to you.',
      },
      {
        title: 'Report and adjust',
        body: 'A plain monthly report and a short call, and next month’s plan changes based on what actually worked.',
      },
    ],
    stack: [{ label: 'Platforms', items: ['Instagram', 'Facebook', 'YouTube', 'LinkedIn'] }],
    work: {
      projects: [],
      lab: ['reels'],
      note: 'Our own reels account is run by a pipeline we built: software that writes, renders and posts short videos. It is a prototype and is labelled as one — it is here because it is the engineering behind a content calendar that never skips a week.',
    },
    faqs: [
      {
        id: 'social-platforms',
        question: 'Which platforms do you manage?',
        answer: [
          'Instagram, Facebook, YouTube and LinkedIn. We would rather recommend the one or two where your customers actually are than spread thin across all four.',
        ],
      },
      {
        id: 'social-shoot',
        question: 'Do you shoot the content, or do I?',
        answer: [
          'Either, or both. We can film with you on a shoot day, work from footage and photos you send, or design content that needs no filming at all. Most businesses end up with a mix.',
        ],
      },
      {
        id: 'social-volume',
        question: 'How many posts a month?',
        answer: [
          'That is agreed in the monthly plan, and depends on the platforms and how much video is involved. We would rather do fewer things well than fill a quota.',
        ],
      },
      {
        id: 'social-approve',
        question: 'Do I have to approve everything?',
        answer: [
          'You approve the monthly plan, and you can review anything before it goes out. Once there is some trust, most people approve the plan and let the posts run.',
        ],
      },
      {
        id: 'social-ads',
        question: 'Does this include ads?',
        answer: [
          'Paid ads are a separate service with their own budget, but the two work best together: whatever does well organically is usually the best raw material for an ad.',
        ],
      },
    ],
    related: ['ugc-influencer-marketing', 'performance-marketing', 'branding'],
    cta: {
      title: 'Send us your handle',
      body: 'Your Instagram or Facebook page is the best brief there is. We will look at it and come back with what we would change first.',
    },
  },

  {
    slug: 'ugc-influencer-marketing',
    group: 'grow',
    name: 'UGC & influencer marketing',
    short: 'UGC & creators',
    icon: 'Users',
    summary:
      'Real people talking about your product: UGC videos for your ads and product pages, and influencer campaigns with creators your customers already follow.',
    includes: [
      'UGC video production',
      'Creator sourcing and briefs',
      'Micro-influencer campaigns',
      'Product seeding',
      'Usage rights and contracts',
      'UGC cut into ads',
    ],
    seo: {
      title: 'UGC & Influencer Marketing Agency for Brands in India | Bitbew',
      description:
        'UGC videos for ads and product pages, and influencer campaigns with micro-creators across Punjab and India — sourced, briefed, contracted and measured.',
    },
    hero: {
      eyebrow: 'UGC & influencer marketing',
      title: 'Real people, talking about your product',
      lede:
        'Customers trust a person holding the product more than a brand describing it. We find the creators, write the briefs, sort out the contracts and usage rights, and turn what comes back into content and ads that sell.',
    },
    artifact: 'brief',
    whatsapp: "Hi Bitbew — I'd like to talk about UGC or influencer marketing.",
    problems: {
      title: 'Why brands ask for this',
      lede: 'Usually one of these.',
      items: [
        {
          head: 'Polished brand ads have stopped working',
          tail: 'People scroll past anything that looks like an advert. A creator filming on a phone gets watched.',
        },
        {
          head: 'You need a lot of video, often',
          tail: 'Ads wear out in weeks, and testing needs several versions of every idea. A studio shoot cannot keep up.',
        },
        {
          head: 'Influencer posts went out and nothing happened',
          tail: 'The wrong creator, a vague brief, no tracking — and no way of knowing whether it was worth the fee.',
        },
        {
          head: 'Creator deals are messy',
          tail: 'Rates, deliverables, deadlines and who owns the video afterwards, agreed over DM and then forgotten.',
        },
      ],
    },
    offer: {
      title: 'Two kinds of creator work',
      lede: 'They overlap, but they are bought for different reasons, so we plan them separately.',
      items: [
        {
          title: 'UGC videos',
          body: 'Unboxings, reviews, demos and testimonial-style videos made by creators for you to use on your ads, product pages and social. You pay for the content, not for their audience.',
        },
        {
          title: 'Influencer campaigns',
          body: 'Creators posting to their own followers — usually micro-influencers whose audience matches your customer, which in India often means regional and Punjabi creators rather than the biggest names.',
        },
        {
          title: 'Sourcing and vetting',
          body: 'Creators chosen for audience fit and genuine engagement rather than follower count, with a look at who actually watches them.',
        },
        {
          title: 'Briefs that work',
          body: 'What to show, what to say, what never to say, the hook and the call to action — clear enough that the first draft is usable.',
        },
        {
          title: 'Contracts and rights',
          body: 'Fees, deliverables, deadlines, and where and for how long you can use the content — agreed in writing before anything is made.',
        },
        {
          title: 'Edits and measurement',
          body: 'Raw clips cut into ad-ready versions, and every campaign given its own links and codes so you can see what it sold.',
        },
      ],
    },
    fit: {
      good: [
        'D2C brands that need a steady supply of video for ads',
        'Launches that need people talking about them in the first few weeks',
        'Brands whose customers follow creators more closely than they follow brands',
      ],
      notFor: [
        'Celebrity endorsements and big-name ambassador deals',
        'Campaigns whose only goal is a follower count',
      ],
    },
    steps: [
      {
        title: 'Plan',
        body: 'What the campaign is for, who it should reach, how many creators and videos, and how we will measure it.',
      },
      {
        title: 'Find and brief',
        body: 'A shortlist of creators for your approval, then briefs, contracts and products sent out.',
      },
      {
        title: 'Produce',
        body: 'Drafts checked against the brief, revisions agreed, final videos delivered and posts scheduled.',
      },
      {
        title: 'Measure and reuse',
        body: 'Results tracked per creator, and the strongest videos cut into ads for your paid campaigns.',
      },
    ],
    stack: [{ label: 'Where it runs', items: ['Instagram Reels', 'YouTube Shorts', 'Meta ads', 'Your product pages'] }],
    faqs: [
      {
        id: 'ugc-difference',
        question: 'What is the difference between UGC and influencer marketing?',
        answer: [
          'With UGC you pay a creator to make content that you post and advertise with — their audience does not matter. With influencer marketing you pay them to post to their own followers.',
          'Many brands start with UGC for their ads, then add influencers once they know which messages work.',
        ],
      },
      {
        id: 'ugc-micro',
        question: 'Micro-influencers or big names?',
        answer: [
          'For most brands, micro-influencers: creators with smaller, loyal audiences in a particular niche or region. They cost less, are easier to work with, and their followers tend to trust them more.',
        ],
      },
      {
        id: 'ugc-rights',
        question: 'Who owns the content creators make?',
        answer: [
          'That depends on what is agreed with each creator, which is exactly why we put usage rights in writing before anything is made: where you can use it, for how long, and whether it can run as a paid ad.',
        ],
      },
      {
        id: 'ugc-guarantee',
        question: 'Can you guarantee sales?',
        answer: [
          'No, and be wary of anyone who does. What we can promise is a proper brief, the right creators, and tracking on every link and code, so you know exactly what each creator brought in.',
        ],
      },
      {
        id: 'ugc-fees',
        question: 'How are creators paid?',
        answer: [
          'Creator fees are agreed with you before anyone is booked, and shown separately from our fee for planning, sourcing, managing and editing.',
        ],
      },
    ],
    related: ['performance-marketing', 'social-media-marketing', 'ecommerce-development'],
    cta: {
      title: 'Tell us about the product',
      body: 'What it is, who buys it and where you sell it. We will tell you whether UGC, influencers or both make sense for you right now.',
    },
  },

  {
    slug: 'performance-marketing',
    group: 'grow',
    name: 'Performance marketing',
    short: 'Ads & lead generation',
    icon: 'Megaphone',
    summary:
      'Google and Meta campaigns set up, watched and reported back in plain numbers, with the landing pages and tracking that make those numbers true.',
    includes: [
      'Google Ads',
      'Meta ads — Facebook and Instagram',
      'Lead generation',
      'Landing pages',
      'Conversion tracking and analytics',
      'Conversion rate optimisation',
    ],
    seo: {
      title: 'Google & Meta Ads Agency in Mohali & Chandigarh | Bitbew',
      description:
        'Performance marketing for businesses in Mohali, Chandigarh and across India: Google and Meta ads, lead generation, landing pages and tracking you can trust.',
    },
    hero: {
      eyebrow: 'Performance marketing — Google & Meta ads',
      title: 'Ads you can see paying for themselves',
      lede:
        'Campaigns on Google, Facebook and Instagram, set up properly, watched every week and reported in rupees rather than likes — with the landing pages and tracking built by engineers, so the numbers in the report can be trusted.',
    },
    artifact: 'report',
    whatsapp: "Hi Bitbew — I'd like to talk about ads.",
    problems: {
      title: 'Where the money leaks',
      lede: 'Ad platforms make spending easy and measuring hard. These are the leaks we see most.',
      items: [
        {
          head: 'You boost posts and hope',
          tail: 'Boosting buys attention, not customers. A campaign needs an objective, an audience and a way to count the result.',
        },
        {
          head: 'Nobody knows which ads work',
          tail: 'Without conversion tracking, the platform optimises for clicks and you pay for people who never buy or call.',
        },
        {
          head: 'The ad is fine; the landing page is not',
          tail: 'A slow page and a vague form waste traffic you have already paid for. This is the part most ad agencies cannot fix themselves.',
        },
        {
          head: 'Leads come in and go cold',
          tail: 'An enquiry answered the next day is often already talking to someone else.',
        },
      ],
    },
    offer: {
      title: 'What we run',
      lede: 'The media spend is yours, paid directly to Google and Meta from your own ad accounts. Our fee covers the work.',
      items: [
        {
          title: 'Google Ads',
          body: 'Search campaigns for people already looking for what you sell, plus Performance Max, Shopping and YouTube where they fit.',
        },
        {
          title: 'Meta ads',
          body: 'Facebook and Instagram campaigns built around creative made for the feed — including UGC — with audiences tested properly.',
        },
        {
          title: 'Lead generation',
          body: 'Campaigns built to bring in enquiries, with forms that ask the right questions and leads sent to your team instantly, on WhatsApp or into your CRM.',
        },
        {
          title: 'Landing pages',
          body: 'Fast, focused pages for each campaign, built by the same people who build our websites, and tested against each other.',
        },
        {
          title: 'Tracking and analytics',
          body: 'Conversion tracking, Google Analytics 4, Meta’s Conversions API and call tracking set up correctly, so every rupee is counted against a result.',
        },
        {
          title: 'Optimisation',
          body: 'Weekly changes to bids, audiences, creative and pages based on what the data says, and a plain report every month.',
        },
      ],
    },
    fit: {
      good: [
        'Businesses with a clear offer, ready to scale it with paid traffic',
        'D2C stores that need sales rather than visits',
        'Service businesses that live on enquiries — clinics, education, real estate, home services',
      ],
      notFor: [
        'Budgets too small to learn anything from — we will say so before you spend it',
        'Businesses without a way to handle the leads that come in',
      ],
    },
    steps: [
      {
        title: 'Audit and tracking',
        body: 'Accounts, pages and tracking checked first. Plenty of campaigns improve before a single ad changes, just by counting properly.',
      },
      {
        title: 'Launch',
        body: 'Campaigns, audiences, creative and landing pages set up in your own accounts, with a clear budget and goal for each.',
      },
      {
        title: 'Optimise weekly',
        body: 'What works gets more budget, what does not gets cut, and new creative is tested all the time.',
      },
      {
        title: 'Report monthly',
        body: 'Spend, results and cost per result in plain numbers, what we changed and why, and the plan for next month.',
      },
    ],
    stack: [
      { label: 'Platforms', items: ['Google Ads', 'Meta Ads Manager', 'YouTube'] },
      { label: 'Measurement', items: ['Google Analytics 4', 'Google Tag Manager', 'Meta Conversions API', 'Search Console'] },
    ],
    work: {
      projects: ['silomin', 'jcharms'],
      note: 'An ad is only as good as the page it lands on. These are stores we built from the checkout up — the kind of page a campaign needs behind it.',
    },
    faqs: [
      {
        id: 'ads-budget',
        question: 'How much should I spend on ads?',
        answer: [
          'Enough for the platform to learn from, which depends on your market and what a customer is worth to you. We suggest a starting budget after the audit, and will tell you honestly if it is too small to test anything.',
        ],
      },
      {
        id: 'ads-accounts',
        question: 'Whose ad accounts are the campaigns in?',
        answer: [
          'Yours. Campaigns run in your own Google and Meta accounts and are paid directly by you. We work inside them with the access you give us.',
        ],
      },
      {
        id: 'ads-time',
        question: 'How soon will I see results?',
        answer: [
          'Search campaigns can bring enquiries within days. Meta campaigns usually need a few weeks of testing before they settle. We report from the first week, so you can see the direction early.',
        ],
      },
      {
        id: 'ads-creative',
        question: 'Do you make the ads themselves?',
        answer: [
          'Yes — static, carousel and video, and UGC through our creator service. Creative is the biggest lever on Meta, so we keep testing it.',
        ],
      },
      {
        id: 'ads-price',
        question: 'How is it priced?',
        answer: [
          'A monthly management fee, quoted after the audit and kept separate from your ad spend, which you pay directly to Google and Meta.',
        ],
      },
    ],
    related: ['ugc-influencer-marketing', 'seo', 'website-development'],
    cta: {
      title: 'Start with a look at the accounts',
      body: 'Tell us where you advertise now, or where you want to. We will look at the accounts and the tracking first, and tell you what we would fix before you spend another rupee.',
    },
  },

  {
    slug: 'seo',
    group: 'grow',
    name: 'SEO & local search',
    short: 'SEO & local search',
    icon: 'Search',
    summary:
      'Technical SEO, useful content and local search — Google Business Profile, maps and listings — so you come up when someone nearby types the thing you sell.',
    includes: [
      'Technical SEO',
      'Local SEO',
      'Google Business Profile',
      'On-page SEO and content',
      'Search Console and analytics',
      'Visibility in AI search',
    ],
    seo: {
      title: 'SEO & Local SEO Services in Mohali & Chandigarh | Bitbew',
      description:
        'SEO for businesses in Mohali, Chandigarh and across India: technical fixes, Google Business Profile and maps, local listings, and content that answers real searches.',
    },
    hero: {
      eyebrow: 'SEO & local SEO services',
      title: 'Come up when someone nearby searches',
      lede:
        'Search engines reward sites that are fast, clear and genuinely useful, and businesses whose details agree everywhere. We fix the technical side like engineers, set up your local presence properly, and write the pages that answer what your customers actually type.',
    },
    artifact: 'serp',
    whatsapp: "Hi Bitbew — I'd like to talk about SEO.",
    problems: {
      title: 'Why you are not showing up',
      lede: 'It is rarely one thing. These are the usual suspects.',
      items: [
        {
          head: 'Google cannot read the site properly',
          tail: 'Heavy page builders, missing titles, pages that only appear after JavaScript runs, no sitemap. The foundations are broken before content even matters.',
        },
        {
          head: 'You are missing from the map',
          tail: 'No Google Business Profile, or a half-filled one with the wrong hours and no photos. For a local search, that map is most of the page.',
        },
        {
          head: 'Every service is crammed onto one page',
          tail: 'So there is nothing specific for Google to rank when someone searches for any one of them.',
        },
        {
          head: 'Your details disagree across the web',
          tail: 'Different phone numbers and addresses on different listings make it harder for search engines to trust any of them.',
        },
      ],
    },
    offer: {
      title: 'What SEO with us covers',
      lede: 'No keyword stuffing, no bought links, and no pages that swap the city name and nothing else. The work that lasts.',
      items: [
        {
          title: 'Technical SEO',
          body: 'Speed, crawling, indexing, redirects, canonicals, structured data, sitemaps and Core Web Vitals — fixed in the code, which is where we are strongest.',
        },
        {
          title: 'Local SEO',
          body: 'Google Business Profile set up and kept active, maps, local listings and consistent details, for people searching around Mohali, Chandigarh or wherever you trade.',
        },
        {
          title: 'Site structure',
          body: 'A page for each thing you sell and each question worth answering, linked together so people and search engines can both find their way.',
        },
        {
          title: 'Content',
          body: 'Service pages and articles written to answer real searches by people who understand the work — not filler written to a word count.',
        },
        {
          title: 'Measurement',
          body: 'Search Console and analytics set up properly, and a monthly report on what you rank for, what people click, and what it brought in.',
        },
        {
          title: 'AI search',
          body: 'Clear, well-structured, factual pages are what AI Overviews and assistants quote. We build for that, without chasing gimmicks.',
        },
      ],
    },
    fit: {
      good: [
        'Local businesses that depend on people nearby finding them',
        'Service companies with several distinct offerings',
        'Sites with good work behind them and nothing in search to show for it',
      ],
      notFor: [
        'Page one in a fortnight — SEO takes months, and anyone promising less is guessing',
        'Bought links or mass-produced location pages, which can get a site penalised',
      ],
    },
    steps: [
      {
        title: 'Audit',
        body: 'A technical crawl, your search data, your local listings and your competitors — and a list of fixes ordered by what will matter most.',
      },
      {
        title: 'Fix the foundations',
        body: 'Technical problems fixed in the code, pages restructured where needed, and your Business Profile and listings put right.',
      },
      {
        title: 'Build out',
        body: 'New and better pages for the services and questions your customers search for, published steadily.',
      },
      {
        title: 'Measure',
        body: 'Monthly reporting from Search Console and analytics, and the next month’s work chosen from what the data shows.',
      },
    ],
    stack: [
      {
        label: 'Tools',
        items: ['Google Search Console', 'Google Analytics 4', 'Google Business Profile', 'Bing Webmaster Tools', 'PageSpeed Insights'],
      },
    ],
    faqs: [
      {
        id: 'seo-time',
        question: 'How long does SEO take?',
        answer: [
          'Technical fixes can show within weeks. Ranking for competitive searches usually takes months of steady work. We report monthly, so you can see progress long before the big results arrive.',
        ],
      },
      {
        id: 'seo-guarantee',
        question: 'Do you guarantee first-page rankings?',
        answer: [
          'No. Nobody controls Google’s results, and guarantees usually come with tactics that get sites penalised later. What we commit to is doing the work search engines reward, and showing you the data.',
        ],
      },
      {
        id: 'seo-local',
        question: 'What is local SEO?',
        answer: [
          'Showing up when people nearby search — in the map results, and for searches like "near me" or "in Mohali". Most of it is your Google Business Profile, consistent listings, reviews, and pages that make clear where you work.',
        ],
      },
      {
        id: 'seo-newsite',
        question: 'Do I need a new website for SEO?',
        answer: [
          'Not always. If the current site is technically sound, we work on it as it is. If it cannot be fixed without fighting it — very slow, unreadable to search engines, impossible to edit — we will say so and explain why.',
        ],
      },
      {
        id: 'seo-ai',
        question: 'What about ChatGPT and AI search?',
        answer: [
          'The things that help with Google — clear structure, accurate facts, pages that genuinely answer questions — are what AI assistants draw on too. We keep your information consistent and machine-readable, and do not sell tricks beyond that.',
        ],
      },
    ],
    related: ['website-development', 'performance-marketing', 'social-media-marketing'],
    cta: {
      title: 'Send us your website',
      body: 'And the searches you wish you showed up for. We will look at why you do not, and what it would take to change that.',
    },
  },
];

export const SERVICE_GROUPS: { id: 'build' | 'grow'; label: string; title: string; lede: string }[] = [
  {
    id: 'build',
    label: 'We build',
    title: 'The thing your business runs on',
    lede: 'Projects with a beginning and an end: priced in fixed stages, built in the open, and handed over in your name.',
  },
  {
    id: 'grow',
    label: 'We grow',
    title: 'The work that brings people to it',
    lede: 'Brand, content, creators, ads and search. Mostly monthly, always quoted separately from a build, and never a condition of one.',
  },
];
